import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock, Copy, Key, Loader2, ShieldAlert, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { DeleteWorkspaceDialog } from "@/components/DeleteWorkspaceDialog";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";

// Common IANA timezones. Kept short + friendly — the raw IANA name is
// what we send to Browserbase, the label is what the owner sees.
const COMMON_TIMEZONES = [
  { value: "", label: "Browserbase default (America/New_York)" },
  { value: "Asia/Kolkata", label: "India — Asia/Kolkata (IST, UTC+5:30)" },
  { value: "Asia/Dubai", label: "UAE — Asia/Dubai (GST, UTC+4)" },
  { value: "Asia/Singapore", label: "Singapore — Asia/Singapore (SGT, UTC+8)" },
  { value: "Asia/Tokyo", label: "Japan — Asia/Tokyo (JST, UTC+9)" },
  { value: "Asia/Shanghai", label: "China — Asia/Shanghai (CST, UTC+8)" },
  { value: "Australia/Sydney", label: "Australia — Australia/Sydney (AEDT/AEST)" },
  { value: "Europe/London", label: "UK — Europe/London (GMT/BST)" },
  { value: "Europe/Paris", label: "Central Europe — Europe/Paris (CET/CEST)" },
  { value: "Europe/Berlin", label: "Germany — Europe/Berlin (CET/CEST)" },
  { value: "America/New_York", label: "US East — America/New_York (EST/EDT)" },
  { value: "America/Chicago", label: "US Central — America/Chicago (CST/CDT)" },
  { value: "America/Denver", label: "US Mountain — America/Denver (MST/MDT)" },
  { value: "America/Los_Angeles", label: "US West — America/Los_Angeles (PST/PDT)" },
  { value: "America/Sao_Paulo", label: "Brazil — America/Sao_Paulo (BRT)" },
  { value: "UTC", label: "UTC" },
];

// BCP-47 locale tags for `navigator.language` + Accept-Language.
const COMMON_LOCALES = [
  { value: "", label: "Browserbase default (en-US)" },
  { value: "en-IN", label: "English (India) — en-IN" },
  { value: "en-GB", label: "English (UK) — en-GB" },
  { value: "en-AU", label: "English (Australia) — en-AU" },
  { value: "en-CA", label: "English (Canada) — en-CA" },
  { value: "en-US", label: "English (US) — en-US" },
  { value: "hi-IN", label: "Hindi — hi-IN" },
  { value: "de-DE", label: "German — de-DE" },
  { value: "fr-FR", label: "French — fr-FR" },
  { value: "es-ES", label: "Spanish (Spain) — es-ES" },
  { value: "es-MX", label: "Spanish (Mexico) — es-MX" },
  { value: "pt-BR", label: "Portuguese (Brazil) — pt-BR" },
  { value: "ja-JP", label: "Japanese — ja-JP" },
  { value: "zh-CN", label: "Chinese (Simplified) — zh-CN" },
  { value: "ar-AE", label: "Arabic (UAE) — ar-AE" },
];

export default function WorkspaceSettingsPage() {
  const { current, refresh, switchTo } = useWorkspace();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [timezone, setTimezone] = useState("");
  const [locale, setLocale] = useState("");
  const [savingLocale, setSavingLocale] = useState(false);
  const [savingProceed, setSavingProceed] = useState(false);
  const [savingGoalFill, setSavingGoalFill] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const myRole = current?.my_role;
  const canManage = myRole === "owner";
  const isPersonal = current?.is_personal === true;
  // Owners of personal workspaces may delete them — Probe Internal + future
  // team workspaces stay locked here.
  const canDelete = canManage && isPersonal;

  useEffect(() => {
    if (!current) return;
    setName(current.name);
    setTimezone(current.browser_timezone || "");
    setLocale(current.browser_locale || "");
  }, [current]);

  const handleRename = async () => {
    if (!name.trim() || name.trim() === current.name) return;
    setSavingName(true);
    try {
      await api.put(`/workspaces/${current.id}`, { name: name.trim() });
      await refresh();
      toast.success("Workspace renamed");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingName(false);
    }
  };

  const localeDirty =
    timezone !== (current?.browser_timezone || "") ||
    locale !== (current?.browser_locale || "");

  const handleSaveLocale = async () => {
    setSavingLocale(true);
    try {
      await api.put(`/workspaces/${current.id}`, {
        // Empty string is our sentinel for "clear the override"; the
        // backend accepts null/empty and stores null.
        browser_timezone: timezone || "",
        browser_locale: locale || "",
      });
      await refresh();
      toast.success("Browser locale updated. New sessions will use these settings.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingLocale(false);
    }
  };

  const handleToggleProceed = async (checked) => {
    setSavingProceed(true);
    try {
      await api.put(`/workspaces/${current.id}`, { proceed_on_confirmation: checked });
      await refresh();
      toast.success(
        checked
          ? "Probe may now proceed through confirmation dialogs in this workspace."
          : "Probe will no longer proceed through confirmation dialogs."
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingProceed(false);
    }
  };

  const handleToggleGoalFill = async (checked) => {
    setSavingGoalFill(true);
    try {
      await api.put(`/workspaces/${current.id}`, { goal_oriented_fill: checked });
      await refresh();
      toast.success(
        checked
          ? "Goal-oriented fill enabled — Probe may pick values for unrecorded required fields."
          : "Goal-oriented fill disabled."
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingGoalFill(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/workspaces/${current.id}`, {
        data: { confirmation_name: current.name },
      });
      toast.success(`Deleted "${current.name}"`);
      // Drop the now-stale stored id, refresh, then route. WorkspaceContext's
      // refresh picks the first remaining workspace (or null) automatically.
      localStorage.removeItem("probe_workspace_id");
      await refresh();
      setDeleteOpen(false);
      navigate("/projects");
    } catch (e) {
      toast.error(formatApiError(e));
      throw e; // let the dialog reset its busy state
    }
  };

  if (!current) return null;

  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10"
      data-testid="workspace-settings-page"
    >
      <div className="mb-8">
        <h1 className="font-['Geist'] font-semibold text-2xl sm:text-[28px] tracking-tight text-[#0F172A]">
          Workspace Settings
        </h1>
        <p className="text-sm text-[#64748B] mt-1.5">
          Manage <span className="font-medium text-[#0F172A]">{current.name}</span> — your role here
          is <span className="capitalize text-[#0F172A]">{myRole}</span>.
        </p>
      </div>

      <div className="surface-card p-5 mb-6 max-w-3xl">
        <label className="text-sm font-medium text-[#475569] block mb-1.5">Workspace name</label>
        <div className="flex gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!canManage}
            className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
            data-testid="workspace-name-input"
          />
          <button
            onClick={handleRename}
            disabled={!canManage || savingName || !name.trim() || name.trim() === current.name}
            data-testid="workspace-name-save"
            className="h-10 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60 flex items-center gap-1.5"
          >
            {savingName && <Loader2 size={13} className="animate-spin" />} Save
          </button>
        </div>
      </div>

      {/* Quick shortcut so people who land here looking for member management
          aren't stranded — Team Members is its own page in the sidebar now. */}
      <Link
        to="/workspace/members"
        data-testid="goto-team-members"
        className="surface-card p-5 max-w-3xl flex items-center justify-between hover:border-[#CBD5E1] transition-colors mb-6"
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#3B82F6]/10 text-[#3B82F6]">
            <Users size={16} />
          </span>
          <div>
            <p className="font-medium text-[#0F172A] text-sm">Team Members</p>
            <p className="text-xs text-[#64748B]">
              {isPersonal
                ? "Personal workspaces don't host teammates."
                : "Invite teammates, change roles, and remove access."}
            </p>
          </div>
        </div>
        <span className="text-xs font-medium text-[#3B82F6]">Open →</span>
      </Link>

      <div className="surface-card p-5 mb-6 max-w-3xl" data-testid="workspace-locale-card">
        <div className="flex items-center gap-2 mb-1.5">
          <Clock size={14} className="text-[#3B82F6]" />
          <h2 className="text-sm font-medium text-[#475569]">
            Browser locale &amp; timezone
          </h2>
        </div>
        <p className="text-xs text-[#64748B] mb-4">
          Applied to every browser session opened for this workspace — recordings,
          replay runs, and re-snapshots. Overrides Browserbase&apos;s US default so
          date pickers, currency formatting, and <code className="text-[11px] font-mono bg-[#F1F5F9] px-1 rounded">Intl.DateTimeFormat</code>{" "}
          on the site under test render in your local timezone.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-[#475569] block mb-1" htmlFor="workspace-timezone-select">
              Timezone
            </label>
            <select
              id="workspace-timezone-select"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={!canManage || savingLocale}
              data-testid="workspace-timezone-select"
              className="w-full h-10 px-3 text-sm rounded-md border border-[#E5E7EB] bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] disabled:opacity-60"
            >
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz.value || "default-tz"} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#475569] block mb-1" htmlFor="workspace-locale-select">
              Locale
            </label>
            <select
              id="workspace-locale-select"
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              disabled={!canManage || savingLocale}
              data-testid="workspace-locale-select"
              className="w-full h-10 px-3 text-sm rounded-md border border-[#E5E7EB] bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] disabled:opacity-60"
            >
              {COMMON_LOCALES.map((loc) => (
                <option key={loc.value || "default-locale"} value={loc.value}>
                  {loc.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
          <p className="text-[11px] text-[#94A3B8]">
            Changes apply to <b>new sessions</b>. Currently running recordings and
            in-flight runs aren&apos;t affected.
          </p>
          <button
            onClick={handleSaveLocale}
            disabled={!canManage || savingLocale || !localeDirty}
            data-testid="workspace-locale-save"
            className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60 flex items-center gap-1.5"
          >
            {savingLocale && <Loader2 size={13} className="animate-spin" />}
            Save locale
          </button>
        </div>
      </div>

      {/* Recovery behaviour — Iter 49 proceed-on-confirmation opt-in. */}
      <div className="surface-card p-5 mb-6 max-w-3xl" data-testid="workspace-recovery-card">
        <div className="flex items-center gap-2 mb-1.5">
          <ShieldAlert size={14} className="text-[#B45309]" />
          <h2 className="text-sm font-medium text-[#475569]">Recovery behaviour</h2>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-[#0F172A]">Proceed through confirmation dialogs</p>
            <p className="text-xs text-[#64748B] mt-0.5 max-w-lg">
              When an unexpected business confirmation dialog (e.g. a &ldquo;Duplicate booking&rdquo;
              warning) blocks replay and the recorded flow continued past that point, allow Probe&apos;s
              recovery engines to click the dialog&apos;s continue/proceed control. Payment, deletion and
              logout controls are never clicked. Each proceed-click is flagged as a{" "}
              <b>business action</b> in the run report.
            </p>
          </div>
          <Switch
            checked={!!current.proceed_on_confirmation}
            disabled={!canManage || savingProceed}
            onCheckedChange={handleToggleProceed}
            data-testid="workspace-proceed-confirmation-toggle"
          />
        </div>
        <div className="flex items-start justify-between gap-4 mt-4 pt-4 border-t border-[#E2E8F0] dark:border-[#334155]">
          <div>
            <p className="text-sm font-medium text-[#0F172A]">Goal-oriented field completion</p>
            <p className="text-xs text-[#64748B] mt-0.5 max-w-lg">
              When a required field blocks replay and the recording holds no value for it (a new
              mandatory field added after recording), allow Probe to pick the most appropriate value
              &mdash; strictly from the control&apos;s <b>own option list</b> (dropdowns/selects only,
              free-text is never invented). Each pick is flagged as an{" "}
              <b>AI-chosen value</b> in the run report.
            </p>
          </div>
          <Switch
            checked={!!current.goal_oriented_fill}
            disabled={!canManage || savingGoalFill}
            onCheckedChange={handleToggleGoalFill}
            data-testid="workspace-goal-fill-toggle"
          />
        </div>
      </div>

      <PersonalAccessTokenSection />

      {canDelete && (
        <div
          data-testid="danger-zone"
          className="max-w-3xl rounded-lg border border-[#FECACA] bg-[#FEF2F2]/40 dark:bg-[#7F1D1D]/10 dark:border-[#7F1D1D]/40"
        >
          <div className="px-5 py-4 border-b border-[#FECACA] dark:border-[#7F1D1D]/40">
            <h2 className="font-['Geist'] font-semibold text-base tracking-tight text-[#991B1B] dark:text-[#FCA5A5]">
              Danger Zone
            </h2>
          </div>
          <div className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#0F172A] dark:text-white">Delete this workspace</p>
              <p className="text-xs text-[#64748B] mt-0.5 dark:text-white/60">
                Permanently removes all projects, tests, runs, and schedules. This action cannot be undone.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              data-testid="open-delete-workspace"
              className="inline-flex items-center gap-1.5 h-9 px-4 text-sm font-medium rounded-md border border-[#EF4444]/30 bg-white text-[#B91C1C] hover:bg-[#FEF2F2] dark:bg-transparent dark:border-[#EF4444]/50 dark:text-[#FCA5A5] dark:hover:bg-[#7F1D1D]/30"
            >
              <Trash2 size={13} /> Delete workspace
            </button>
          </div>
        </div>
      )}

      <DeleteWorkspaceDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        workspaceName={current.name}
        onConfirm={handleDelete}
      />
    </div>
  );
}


// -------------------------------------------------------------------
// Iter 71.5.6 — Personal Access Token section
// -------------------------------------------------------------------
// Exposes a copy-friendly bearer token for use with the "Copy from URL"
// test-import flow (and any other API automation). Tokens are minted
// server-side via POST /api/auth/access-token, share the same 12-hour
// validity as the login-response `access_token`, and are NOT stored
// anywhere new — they're a fresh JWT signed with the same secret as
// the current session. No persistence, no revocation UI — if you need
// to invalidate, log out (which rotates the session cookies but not
// existing bearer tokens; a compromised token remains valid for its
// 12-hour lifetime, which matches Probe's session-token policy today).
function PersonalAccessTokenSection() {
  const [token, setToken] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleMint = async () => {
    setLoading(true);
    try {
      const r = await api.post("/auth/access-token");
      setToken(r.data?.access_token || "");
      const ttl = r.data?.expires_in || 43200;
      setExpiresAt(new Date(Date.now() + ttl * 1000));
      toast.success("Access token generated — copy it now (visible only once per generation)");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Token copied to clipboard");
    } catch {
      toast.error("Couldn't copy — select the token manually to copy");
    }
  };

  return (
    <div
      data-testid="personal-access-token-section"
      className="max-w-3xl rounded-lg border border-[#E2E8F0] bg-white dark:bg-[#0F172A]/60 dark:border-white/10"
    >
      <div className="px-5 py-4 border-b border-[#E2E8F0] dark:border-white/10">
        <h2 className="font-['Geist'] font-semibold text-base tracking-tight text-[#0F172A] dark:text-white flex items-center gap-2">
          <Key size={15} className="text-[#3B82F6]" />
          Personal access token
        </h2>
        <p className="text-xs text-[#64748B] dark:text-white/60 mt-1">
          A fresh 12-hour bearer token for API automation and the
          <span className="mx-1 font-medium">Copy&nbsp;from&nbsp;URL</span> test-import flow.
          Paste into the destination deployment&#39;s dialog as the
          <span className="ml-1 font-mono">auth_bearer</span>.
        </p>
      </div>
      <div className="px-5 py-4 space-y-3">
        {token ? (
          <div className="space-y-2">
            <div className="flex items-stretch gap-2">
              <input
                type="text"
                readOnly
                value={token}
                data-testid="personal-access-token-value"
                onClick={(e) => e.target.select()}
                className="flex-1 h-10 px-3 rounded-md border border-[#CBD5E1] text-xs font-mono bg-[#F8FAFC] dark:bg-[#0F172A] dark:border-white/10 dark:text-white"
              />
              <button
                type="button"
                onClick={handleCopy}
                data-testid="personal-access-token-copy"
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-[#CBD5E1] text-sm font-medium text-[#334155] hover:bg-[#F1F5F9] dark:border-white/10 dark:text-white dark:hover:bg-white/10"
              >
                <Copy size={13} />
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            {expiresAt && (
              <p className="text-[11px] text-[#94A3B8]">
                Expires at {expiresAt.toLocaleString()} ({Math.round((expiresAt - new Date()) / 60000)}&nbsp;min from now).
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-[#64748B] dark:text-white/60">
            No active token displayed. Click <b>Generate token</b> to mint a fresh 12-hour JWT.
          </p>
        )}
        <button
          type="button"
          onClick={handleMint}
          disabled={loading}
          data-testid="personal-access-token-generate"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB] disabled:opacity-60"
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : <Key size={13} />}
          {loading ? "Minting..." : (token ? "Regenerate token" : "Generate token")}
        </button>
      </div>
    </div>
  );
}
